import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, Lock, ShieldCheck, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { recoverPassword, signup } from "@/services/auth";

export default function SignInGate() {
  const { login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  // Challenge state carried between login attempts (returned by the server).
  const [mfaId, setMfaId] = useState("");
  const [mfaType, setMfaType] = useState(undefined);
  const [captcha, setCaptcha] = useState(null);

  // phase: 'credentials' → optional 'captcha' / 'mfa' follow-up
  const [phase, setPhase] = useState("credentials");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Sub-views. mode: 'signin' | 'forgot' | 'signup'
  const [mode, setMode] = useState("signin");
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverSent, setRecoverSent] = useState(false);

  // Signup fields (no password — the activation email sets it).
  const [suEmail, setSuEmail] = useState("");
  const [suFirstName, setSuFirstName] = useState("");
  const [suLastName, setSuLastName] = useState("");
  const [signupSent, setSignupSent] = useState(false);

  function goToForgot() {
    setError("");
    setRecoverSent(false);
    setRecoverEmail(username);
    setMode("forgot");
  }

  function goToSignup() {
    setError("");
    setSignupSent(false);
    setSuEmail(username);
    setMode("signup");
  }

  function backToSignin() {
    setError("");
    setMode("signin");
  }

  async function handleSignup(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signup({ email: suEmail, firstName: suFirstName, lastName: suLastName });
      setSignupSent(true);
    } catch (err) {
      setError(err.message || "Sign up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRecover(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await recoverPassword({ email: recoverEmail });
      // Don't reveal whether the account exists — always show the same confirmation.
      setRecoverSent(true);
    } catch (err) {
      setError(err.message || "Could not send the reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function submitLogin(extra = {}) {
    setLoading(true);
    setError("");
    try {
      const result = await login({
        username,
        password,
        captchaCode,
        mfaId,
        mfaCode,
        mfaType,
        ...extra,
      });

      if (result.status === "success") {
        // AuthContext.loadSession() flips isAuthenticated → the app re-renders.
        return;
      }

      if (result.status === "mfa_required") {
        setMfaId(result.mfaId || "");
        setMfaType(result.mfaType);
        setPhase("mfa");
        return;
      }

      if (result.status === "captcha_required") {
        setCaptcha(result.captcha || null);
        setPhase("captcha");
        return;
      }

      setError("Unexpected login response. Please try again.");
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    submitLogin();
  }

  const showCaptcha = phase === "captcha";
  const showMfa = phase === "mfa";
  const captchaImageSrc =
    typeof captcha === "string" && (captcha.startsWith("data:") || captcha.startsWith("http"))
      ? captcha
      : null;

  if (mode === "forgot") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8 w-full max-w-md">
          <img
            src="https://media.base44.com/images/public/6a3e1c26ec9e714e5b52016b/70232ae49_SELISE_Logo_1.svg"
            alt="SELISE Group"
            className="h-12 w-auto mx-auto mb-5"
          />
          <h1 className="text-xl font-bold text-gray-900 text-center">Reset your password</h1>
          <p className="text-sm text-gray-500 mt-1.5 mb-6 text-center">
            {recoverSent
              ? "If an account exists for that email, we've sent a reset link."
              : "Enter your email and we'll send you a reset link."}
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm" role="alert">
              {error}
            </div>
          )}

          {recoverSent ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Check your inbox and follow the link to choose a new password. The link
                expires after a short while.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 font-medium"
                onClick={backToSignin}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleRecover} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recoverEmail">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="recoverEmail"
                    name="recoverEmail"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    value={recoverEmail}
                    onChange={(e) => setRecoverEmail(e.target.value)}
                    disabled={loading}
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12 font-medium" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>

              <button
                type="button"
                onClick={backToSignin}
                className="w-full text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (mode === "signup") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8 w-full max-w-md">
          <img
            src="https://media.base44.com/images/public/6a3e1c26ec9e714e5b52016b/70232ae49_SELISE_Logo_1.svg"
            alt="SELISE Group"
            className="h-12 w-auto mx-auto mb-5"
          />
          <h1 className="text-xl font-bold text-gray-900 text-center">Create your account</h1>
          <p className="text-sm text-gray-500 mt-1.5 mb-6 text-center">
            {signupSent
              ? "Almost there — check your email"
              : "We'll email you a link to finish setting up your account"}
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm" role="alert">
              {error}
            </div>
          )}

          {signupSent ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                We've sent an activation link to <strong>{suEmail}</strong>. Follow it to
                set your password and finish creating your account.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 font-medium"
                onClick={backToSignin}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="suFirstName">First name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="suFirstName"
                    name="suFirstName"
                    type="text"
                    autoComplete="given-name"
                    autoFocus
                    value={suFirstName}
                    onChange={(e) => setSuFirstName(e.target.value)}
                    disabled={loading}
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="suLastName">Last name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="suLastName"
                    name="suLastName"
                    type="text"
                    autoComplete="family-name"
                    value={suLastName}
                    onChange={(e) => setSuLastName(e.target.value)}
                    disabled={loading}
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="suEmail">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="suEmail"
                    name="suEmail"
                    type="email"
                    autoComplete="email"
                    value={suEmail}
                    onChange={(e) => setSuEmail(e.target.value)}
                    disabled={loading}
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12 font-medium" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account…
                  </>
                ) : (
                  "Sign up"
                )}
              </Button>

              <button
                type="button"
                onClick={backToSignin}
                className="w-full text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                Already have an account? Sign in
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8 w-full max-w-md">
        <img
          src="https://media.base44.com/images/public/6a3e1c26ec9e714e5b52016b/70232ae49_SELISE_Logo_1.svg"
          alt="SELISE Group"
          className="h-12 w-auto mx-auto mb-5"
        />
        <h1 className="text-xl font-bold text-gray-900 text-center">
          Welcome to SELISE Group Official Organogram
        </h1>
        <p className="text-sm text-gray-500 mt-1.5 mb-6 text-center">
          {showMfa ? "Enter your verification code" : "Sign in to continue"}
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Email</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
              <Input
                id="username"
                name="username"
                type="email"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading || showMfa}
                className="pl-10 h-12"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || showMfa}
                className="pl-10 h-12"
                required
              />
            </div>
          </div>

          {phase === "credentials" && (
            <div className="text-right -mt-1">
              <button
                type="button"
                onClick={goToForgot}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}

          {showCaptcha && (
            <div className="space-y-2">
              <Label htmlFor="captchaCode">Captcha</Label>
              {captchaImageSrc && (
                <img
                  src={captchaImageSrc}
                  alt="Captcha challenge"
                  className="h-12 rounded border border-gray-200 bg-gray-50"
                />
              )}
              <Input
                id="captchaCode"
                name="captchaCode"
                type="text"
                inputMode="text"
                autoComplete="off"
                placeholder="Enter the characters shown"
                value={captchaCode}
                onChange={(e) => setCaptchaCode(e.target.value)}
                disabled={loading}
                className="h-12"
                required
              />
            </div>
          )}

          {showMfa && (
            <div className="space-y-2">
              <Label htmlFor="mfaCode">Verification code</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                <Input
                  id="mfaCode"
                  name="mfaCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  disabled={loading}
                  className="pl-10 h-12 tracking-widest"
                  required
                />
              </div>
            </div>
          )}

          <Button type="submit" className="w-full h-12 font-medium" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {showMfa ? "Verifying…" : "Signing in…"}
              </>
            ) : showMfa ? (
              "Verify"
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        {!showMfa && (
          <p className="mt-5 text-center text-sm text-gray-500">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              onClick={goToSignup}
              className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
            >
              Sign up
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
